import { useState } from 'react';
import { locationService } from '@/services/locationService';
import { backendJobService } from '@/services/backendJobService';

export function ApiTestComponent() {
  const [results, setResults] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const testEndpoint = async (name: string, testFn: () => Promise<any>) => {
    setLoading(prev => ({ ...prev, [name]: true }));
    try {
      const result = await testFn();
      setResults(prev => ({ ...prev, [name]: { success: true, data: result } }));
      console.log(`✅ ${name} test passed:`, result);
    } catch (error) {
      setResults(prev => ({ ...prev, [name]: { success: false, error: error instanceof Error ? error.message : 'Unknown error' } }));
      console.error(`❌ ${name} test failed:`, error);
    } finally {
      setLoading(prev => ({ ...prev, [name]: false }));
    }
  };

  const tests = [
    {
      name: 'Counties',
      test: () => locationService.getCounties()
    },
    {
      name: 'Categories',
      test: () => backendJobService.getHomepageCategories()
    },
    {
      name: 'Areas',
      test: () => locationService.getAreas()
    },
    {
      name: 'Subcategories (Sample)',
      test: async () => {
        // First get categories to get a category ID
        const categories = await backendJobService.getHomepageCategories();
        if (categories.length === 0) {
          throw new Error('No categories available to test subcategories');
        }
        const firstCategory = categories[0];
        console.log('Testing subcategories for category:', firstCategory.name, 'ID:', firstCategory.id);
        return await backendJobService.getCategorySubcategories(firstCategory.id);
      }
    }
  ];

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-xl font-bold mb-4">API Endpoint Tests</h2>
      
      <div className="space-y-4">
        {tests.map(({ name, test }) => (
          <div key={name} className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">{name}</h3>
              <button
                onClick={() => testEndpoint(name, test)}
                disabled={loading[name]}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {loading[name] ? 'Testing...' : 'Test'}
              </button>
            </div>
            
            {results[name] && (
              <div className={`mt-2 p-3 rounded ${results[name].success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {results[name].success ? (
                  <div>
                    <p className="text-green-700 font-medium">✅ Success</p>
                    <pre className="text-xs mt-2 text-green-600 overflow-auto">
                      {JSON.stringify(results[name].data, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <div>
                    <p className="text-red-700 font-medium">❌ Error</p>
                    <p className="text-red-600 text-sm mt-1">{results[name].error}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold mb-2">Current API Configuration</h3>
        <div className="text-sm text-gray-600 space-y-1">
          <p><strong>Base URL:</strong> https://npm.rapidfix.ie</p>
          <p><strong>Counties:</strong> /api/v1/public/counties</p>
          <p><strong>Categories:</strong> /api/v1/public/categories/homepage</p>
          <p><strong>Subcategories:</strong> /api/v1/public/categories/{`{id}`}/subcategories</p>
          <p><strong>Areas:</strong> /api/v1/public/areas</p>
          <p><strong>Job Creation:</strong> /api/v1/auth/jobs (requires authentication)</p>
        </div>
      </div>
    </div>
  );
}